import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { PassportMark } from "../components/PassportMark";

export function LoginPage() {
  const { requestOtp, verifyOtp, loginPassword } = useAuth();
  const [step, setStep] = useState<"identify" | "code">("identify");
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [hint, setHint] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setError("");
  }, [step]);

  async function onSendCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await requestOtp(identifier.trim());
      if (!data.sent) {
        setError(data.message || "No account found, or code could not be sent.");
        return;
      }
      setHint(data.dev_code ? `${data.message} (dev ${data.dev_code})` : data.message);
      setStep("code");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await verifyOtp(identifier.trim(), code.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onPassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await loginPassword(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-screen">
      <header className="login-mast">
        <PassportMark className="passport-mark login-mark" />
        <div>
          <h1 className="login-title">Surgical Passport</h1>
          <p className="login-lock">
            Patient · Procedure · Site
            <span aria-hidden="true"> · </span>
            Verified · Locked · Secure
          </p>
        </div>
      </header>

      <main className="login-panel">
        <h2 className="login-panel-title">Staff sign-in</h2>
        <p className="login-panel-copy">Clinic email, phone, or office password.</p>

        {step === "identify" ? (
          <form onSubmit={onSendCode}>
            <label htmlFor="id">Email or phone</label>
            <input
              id="id"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
              required
            />
            <button className="primary" type="submit" disabled={busy}>
              {busy ? "Sending…" : "Send access code"}
            </button>
          </form>
        ) : (
          <form onSubmit={onVerify}>
            <label htmlFor="code">Code for {identifier}</label>
            <input
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              required
            />
            <button className="primary" type="submit" disabled={busy}>
              {busy ? "Unlocking…" : "Unlock"}
            </button>
            <button type="button" className="linkish" onClick={() => setStep("identify")}>
              Use a different email / phone
            </button>
            {hint && <div className="hint">{hint}</div>}
          </form>
        )}

        <div className="login-rule" role="separator" />
        <p className="login-alt">Password</p>

        <form onSubmit={onPassword}>
          <label htmlFor="email">Email</label>
          <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button className="primary" type="submit" disabled={busy}>
            Sign in with password
          </button>
        </form>

        {error && <div className="error">{error}</div>}
      </main>
    </div>
  );
}
